import * as React from "react";
import { useEffect, useMemo, useState } from "react";

import { Badge, Body1, Radio, RadioGroup, Title1 } from "@fluentui/react-components";
import {
  ArrowUndo24Regular,
  CheckmarkCircle24Regular,
  ChevronLeft16Filled,
  ChevronRight16Filled,
  DismissCircle24Regular,
  Eye24Regular,
  Save24Regular,
} from "@fluentui/react-icons";
import { useNavigate } from "react-router-dom";

import { useStyles } from "./styles";
import ButtonComponent from "../../../../components/Button/Button";
import { getResolutionStatusDisplayText } from "../../../../lib/strings";
import {
  AnalyzeResult,
  CaseReference,
  CitationType,
  ResolutionStatus,
  ResolvedLawCase,
} from "../../../../models/benchly.models";
import { CitationStorageService } from "../../../../service/citationStorage.service";
import { DocumentActionService } from "../../../../service/documentActions.service";
import { useCitationStore } from "../../../../store/CitationStore";

const Resolving: React.FC = () => {
  const styles = useStyles();
  const navigate = useNavigate();
  const { citations: allCitations, updateCitation } = useCitationStore();

  // Helper function to aggregate all foundDocs from all citations
  const getAllFoundDocs = (citation: AnalyzeResult): CaseReference[] => {
    const cases = (citation.citations ?? []).filter(Boolean);
    if (cases.length === 0) return [];
    // Aggregate all foundDocs from all LawCase entries in the citation
    const allDocs: CaseReference[] = [];
    for (const lawCase of cases) {
      const docs = (lawCase.foundDocs ?? []).filter(Boolean);
      if (docs.length > 0) allDocs.push(...docs);
    }
    return allDocs;
  };

  // Helper function to determine overall status for parallel citations
  const getOverallStatus = (citation: AnalyzeResult): ResolutionStatus => {
    const cases = (citation.citations ?? []).filter(Boolean);
    if (cases.length === 0) return ResolutionStatus.NotFound;
    // If any citation needs verification, overall needs verification
    if (cases.some((c) => c.status === ResolutionStatus.NeedsVerification)) {
      return ResolutionStatus.NeedsVerification;
    }
    // If any requires manual review
    if (cases.some((c) => c.status === ResolutionStatus.RequiresManualReview)) {
      return ResolutionStatus.RequiresManualReview;
    }
    // If all are certified
    if (cases.every((c) => c.status === ResolutionStatus.ValidatedAndCertified)) {
      return ResolutionStatus.ValidatedAndCertified;
    }
    // Default to the first citation's status
    return cases[0].status;
  };

  // Filter only citations with NeedsVerification status (keep all, even with resolvedLawCase)
  const citations = useMemo(() => {
    return allCitations.filter(
      (result) =>
        getOverallStatus(result) === ResolutionStatus.NeedsVerification ||
        getOverallStatus(result) === ResolutionStatus.RequiresManualReview
    );
  }, [allCitations]);

  const [currentIndex, setCurrentIndex] = useState(0);
  const [selectedDocId, setSelectedDocId] = useState<string | null>(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const dialogRef = React.useRef<Office.Dialog | null>(null);

  // Ensure the index is always within the bounds of the filtered citations array
  const boundedIndex = useMemo(() => {
    if (citations.length === 0) {
      return 0;
    }
    return Math.min(currentIndex, citations.length - 1);
  }, [citations.length, currentIndex]);

  // Keep the stored currentIndex in sync with the bounded index when the list changes
  useEffect(() => {
    if (currentIndex !== boundedIndex) {
      setCurrentIndex(boundedIndex);
    }
  }, [boundedIndex, currentIndex]);
  const currentCitation = citations[boundedIndex];
  const safeCitations = useMemo(
    () => (currentCitation?.citations ?? []).filter(Boolean),
    [currentCitation]
  );

  const firstCitation = safeCitations[0];
  const handleDialogEvent = (_arg: { error: number }) => {
    if (dialogRef.current) {
      dialogRef.current.close();
      dialogRef.current = null;
    }
  };

  // When navigating to a citation that already has a resolvedLawCase, pre-select it
  useEffect(() => {
    if (currentCitation?.resolvedLawCase && currentCitation.resolvedLawCase !== "none") {
      // For resolved citations, try to find the selected doc in foundDocs
      const allDocs = getAllFoundDocs(currentCitation);
      const resolved = currentCitation.resolvedLawCase; // Type narrowing
      const resolvedDoc = allDocs.find(
        (doc) =>
          doc.identifier === resolved.identifierOnDocument ||
          doc.docId === resolved.foundDocs?.[0]?.docId
      );
      if (resolvedDoc) {
        setSelectedDocId(resolvedDoc.docId);
      }
    } else {
      setSelectedDocId(null);
    }

    if (currentCitation?.documentReferenceId) {
      DocumentActionService.selectCitation(currentCitation.documentReferenceId);
    }
  }, [currentIndex]);

  const handleNext = () => {
    if (currentIndex < citations.length - 1) {
      setCurrentIndex(currentIndex + 1);
    }
  };

  const handleBack = () => {
    if (currentIndex > 0) {
      setCurrentIndex(currentIndex - 1);
    }
  };

  const handleDocSelection = (docId: string) => {
    setSelectedDocId(docId);
  };

  const handleSelect = async () => {
    if (!selectedDocId || !currentCitation || isProcessing) return;

    setIsProcessing(true);
    try {
      // Find the selected document from all foundDocs
      const allDocs = getAllFoundDocs(currentCitation);
      const selectedDoc = allDocs.find((doc) => doc.docId === selectedDocId);
      if (!selectedDoc) {
        console.error("Selected document not found");
        return;
      }

      // Create ResolvedLawCase - for parallel citations, this represents the chosen case
      // which applies to all citations in the parallel group
      const resolvedLawCase: ResolvedLawCase = {
        identifierOnDocument: selectedDoc.identifier,
        titleOnDocument: selectedDoc.title,
        status: ResolutionStatus.ValidatedAndCertified,
        foundDocs: [selectedDoc],
      };

      // Create updated citation with resolvedLawCase
      const updatedCitation: AnalyzeResult = {
        ...currentCitation,
        resolvedLawCase,
        lastUpdated: new Date().toISOString(),
      };

      // Update XML storage
      await CitationStorageService.updateCitationResult(updatedCitation);

      // Update context
      updateCitation(updatedCitation);

      console.log("Citation resolved with selected case (pending save):", selectedDoc.title);
    } catch (error) {
      console.error("Error selecting case:", error);
    } finally {
      setIsProcessing(false);
    }
  };

  const handleIgnore = async () => {
    if (!currentCitation || isProcessing) return;

    setIsProcessing(true);
    try {
      // Create updated citation with resolvedLawCase set to 'none'
      const updatedCitation: AnalyzeResult = {
        ...currentCitation,
        resolvedLawCase: "none",
        lastUpdated: new Date().toISOString(),
      };

      // Update XML storage
      await CitationStorageService.updateCitationResult(updatedCitation);

      // Update context
      updateCitation(updatedCitation);

      console.log(
        "Citation marked as ignored (pending save):",
        currentCitation.citations?.[0]?.identifierOnDocument || "unknown"
      );
    } catch (error) {
      console.error("Error ignoring citation:", error);
    } finally {
      setIsProcessing(false);
    }
  };

  const handleUndo = async () => {
    if (!currentCitation || isProcessing) return;

    setIsProcessing(true);
    try {
      // Clear the resolvedLawCase
      const updatedCitation: AnalyzeResult = {
        ...currentCitation,
        resolvedLawCase: undefined,
        lastUpdated: new Date().toISOString(),
      };

      // Update XML storage
      await CitationStorageService.updateCitationResult(updatedCitation);

      // Update context
      updateCitation(updatedCitation);

      // Reset selection
      setSelectedDocId(null);

      console.log(
        "Undid resolution for:",
        currentCitation.citations?.[0]?.identifierOnDocument || "unknown"
      );
    } catch (error) {
      console.error("Error undoing:", error);
    } finally {
      setIsProcessing(false);
    }
  };

  const handleViewCase = (caseId: string) => {
    if (!window.Office?.context?.ui?.displayDialogAsync) {
      console.error("Office.js dialog API not available.");
      return;
    }
    // Store the docId to load HTML on demand in the dialog
    const storageKey = `view-case-dialog-docId`;
    try {
      localStorage.setItem(storageKey, caseId);
    } catch (e) {
      console.error("Failed to save case docId to localStorage", e);
      return;
    }
    const url = `${window.location.origin}/dialog.html?dialog=view-html-case`;
    window.Office.context.ui.displayDialogAsync(
      url,
      {
        height: 60,
        width: 60,
        displayInIframe: true,
      },
      (asyncResult) => {
        if (asyncResult.status !== window.Office.AsyncResultStatus.Succeeded) {
          console.error(asyncResult.error?.message || "Dialog failed to open.");
          return;
        }
        const dialog = asyncResult.value;
        dialogRef.current = dialog;
        dialog.addEventHandler(window.Office.EventType.DialogEventReceived, handleDialogEvent);
      }
    );
  };

  // Check resolution state - "pending save" means has resolvedLawCase but status is still NeedsVerification
  const isResolved = currentCitation?.resolvedLawCase !== undefined;
  const isIgnored = currentCitation?.resolvedLawCase === "none";
  const hasSelectedCase = isResolved && !isIgnored;

  // A citation has pending changes if it has a resolvedLawCase set
  const hasPendingChanges = isResolved;

  const formatDate = (dateStr: string) => {
    if (!dateStr) return "N/A";
    try {
      const date = new Date(dateStr);
      return date.toLocaleDateString("en-US", {
        year: "numeric",
        month: "short",
        day: "numeric",
      });
    } catch {
      return dateStr;
    }
  };

  const getStatusColor = (status: ResolutionStatus) => {
    switch (status) {
      case ResolutionStatus.ValidatedAndCertified:
        return "#107c10";
      case ResolutionStatus.NeedsVerification:
      case ResolutionStatus.RequiresManualReview:
        return "#ff8c00";
      case ResolutionStatus.NotFound:
        return "#d13438";
      default:
        return "#605e5c";
    }
  };

  if (citations.length === 0) {
    return (
      <div className={styles.container}>
        <Title1>Resolving</Title1>
        <Body1>No citations need verification at this time.</Body1>
      </div>
    );
  }

  // Count how many citations have been resolved (pending save) - those with resolvedLawCase set
  const pendingCount = citations.filter((c) => c.resolvedLawCase !== undefined).length;

  return (
    <div className={styles.container}>
      {/* Sticky header section */}
      <div className={styles.stickyHeader}>
        <Title1>Resolving</Title1>

        {/* Pending changes indicator */}
        {pendingCount > 0 && (
          <div
            style={{
              backgroundColor: "#fff4ce",
              padding: "8px 12px",
              borderRadius: "4px",
              border: "1px solid #ffb900",
            }}
          >
            <Body1 style={{ color: "#6b5700" }}>
              <strong>{pendingCount}</strong> citation{pendingCount > 1 ? "s" : ""} with pending
              changes
            </Body1>
          </div>
        )}

        {/* Navigation with position indicator */}
        <div className={styles.navigationContainer}>
          <ButtonComponent
            variant="outline-primary"
            onClick={handleBack}
            disabled={currentIndex === 0}
            size="small"
            icon={<ChevronLeft16Filled />}
          >
            Back
          </ButtonComponent>
          <Body1 className={styles.positionIndicator}>
            <strong>{currentIndex + 1}</strong> of <strong>{citations.length}</strong>
          </Body1>
          <ButtonComponent
            variant="outline-primary"
            onClick={handleNext}
            disabled={currentIndex === citations.length - 1}
            size="small"
            iconPosition="after"
            icon={<ChevronRight16Filled />}
          >
            Next
          </ButtonComponent>
        </div>

        {/* Action bar */}
        <div className={styles.actionBar}>
          <button
            className={styles.actionButton}
            onClick={handleSelect}
            disabled={!selectedDocId || isProcessing}
            title="Select"
            style={
              !selectedDocId || isProcessing ? { cursor: "not-allowed", opacity: 0.5 } : undefined
            }
          >
            <CheckmarkCircle24Regular />
            <span>Select</span>
          </button>
          <button
            className={styles.actionButton}
            onClick={handleIgnore}
            disabled={isProcessing}
            title="Ignore"
            style={isProcessing ? { cursor: "not-allowed", opacity: 0.5 } : undefined}
          >
            <DismissCircle24Regular />
            <span>Ignore</span>
          </button>
          <button
            className={styles.actionButton}
            onClick={handleUndo}
            disabled={!hasPendingChanges || isProcessing}
            title="Undo"
            style={
              !hasPendingChanges || isProcessing
                ? { cursor: "not-allowed", opacity: 0.5 }
                : undefined
            }
          >
            <ArrowUndo24Regular />
            <span>Undo</span>
          </button>
          <button
            className={styles.actionButton}
            onClick={() => navigate("/save-pending")}
            disabled={pendingCount === 0 || isProcessing}
            title="Save"
            style={
              pendingCount === 0 || isProcessing
                ? { cursor: "not-allowed", opacity: 0.5 }
                : { color: "#107c10" }
            }
          >
            <Save24Regular />
            <span>Save</span>
          </button>
        </div>
      </div>

      {/* Scrollable content section */}
      <div className={styles.scrollableContent}>
        {/* Current citation details */}
        <div className={styles.citationCard}>
          <div className={styles.citationHeader}>
            {/* Citation Type Badge */}
            <div style={{ display: "flex", alignItems: "center", gap: "8px", flexWrap: "wrap" }}>
              <Badge
                appearance="outline"
                color={
                  currentCitation.citationType === CitationType.ParallelCites
                    ? "brand"
                    : "informative"
                }
                size="small"
              >
                {currentCitation.citationType === CitationType.ParallelCites
                  ? "Parallel Citations"
                  : "Single Citation"}
              </Badge>
              {hasPendingChanges && (
                <Badge appearance="filled" color="warning" size="small">
                  Pending Save
                </Badge>
              )}
              {isIgnored && (
                <Badge appearance="filled" color="danger" size="small">
                  Ignored
                </Badge>
              )}
              {hasSelectedCase && (
                <Badge appearance="filled" color="success" size="small">
                  Selected
                </Badge>
              )}
            </div>

            {/* Title */}
            {firstCitation?.titleOnDocument && (
              <Body1 className={styles.title}>{firstCitation.titleOnDocument}</Body1>
            )}

            {/* Display all identifiers for parallel citations */}
            {currentCitation.citationType === CitationType.ParallelCites &&
            currentCitation.citations &&
            currentCitation.citations.length > 1 ? (
              <div style={{ marginTop: "8px" }}>
                <Body1 style={{ fontWeight: "600", marginBottom: "4px" }}>Citations:</Body1>
                {safeCitations.map((cite, idx) => (
                  <Body1
                    key={idx}
                    className={styles.identifier}
                    style={{ marginLeft: "12px", fontSize: "13px" }}
                  >
                    • {cite.identifierOnDocument}
                  </Body1>
                ))}
              </div>
            ) : (
              <Body1 className={styles.identifier}>
                {firstCitation?.identifierOnDocument || "Unknown"}
              </Body1>
            )}

            <Body1
              style={{
                color: getStatusColor(getOverallStatus(currentCitation)),
                fontWeight: "600",
              }}
            >
              Status: {getResolutionStatusDisplayText(getOverallStatus(currentCitation))}
            </Body1>
            {currentCitation?.resolvedLawCase && currentCitation.resolvedLawCase !== "none" && (
              <Body1 style={{ color: "#107c10", fontSize: "12px", marginTop: "4px" }}>
                → Selected: {currentCitation.resolvedLawCase.titleOnDocument || "(missing title)"}
              </Body1>
            )}
          </div>

          {/* Found documents */}
          <div className={styles.foundDocsSection}>
            <Body1 className={styles.sectionTitle}>
              Found Documents ({getAllFoundDocs(currentCitation).length || 0})
            </Body1>

            {getAllFoundDocs(currentCitation).length > 0 ? (
              <RadioGroup
                value={selectedDocId || ""}
                onChange={(_e, data) => handleDocSelection(data.value)}
              >
                {getAllFoundDocs(currentCitation).map((doc: CaseReference) => (
                  <div
                    key={doc.docId}
                    className={
                      selectedDocId === doc.docId ? styles.docItemSelected : styles.docItem
                    }
                    onClick={() => handleDocSelection(doc.docId)}
                  >
                    <Radio value={doc.docId} />
                    <div className={styles.docInfo}>
                      <Body1 className={styles.docTitle}>{doc.title}</Body1>
                      <Body1 className={styles.docMeta}>
                        <span>
                          <strong>Identifier:</strong> {doc.identifier}
                        </span>
                      </Body1>
                      <Body1 className={styles.docMeta}>
                        <span>
                          <strong>Court:</strong> {doc.court || doc.courtAbbreviation || "N/A"}
                        </span>
                      </Body1>
                      <Body1 className={styles.docMeta}>
                        <span>
                          <strong>Date:</strong> {formatDate(doc.docDate)}
                        </span>
                      </Body1>
                    </div>
                    <div className={styles.viewCaseButtonContainer}>
                      <button
                        className={styles.actionButton}
                        onClick={(e) => {
                          e.stopPropagation();
                          handleViewCase(doc.docId);
                        }}
                        title="View Case"
                      >
                        <Eye24Regular />
                        <span>View</span>
                      </button>
                    </div>
                  </div>
                ))}
              </RadioGroup>
            ) : (
              <Body1 style={{ color: "#605e5c", fontStyle: "italic" }}>
                No matching documents found.
              </Body1>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Resolving;
